import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { SearchInput } from '../components/SearchInput';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { ComposeBroadcastDialog } from '../components/dialogs/ComposeBroadcastDialog';
import { GiftDialog } from '../components/dialogs/GiftDialog';
import { UsersAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { User, Page } from '../types/api';
import { formatDate } from '../utils/format';
import './common.css';

export function UsersPage() {
  const [acting, setActing] = useState<{ user: User; kind: 'suspend' | 'unsuspend' } | null>(null);
  const [messaging, setMessaging] = useState<User | null>(null);
  const [gifting, setGifting] = useState<User | null>(null);
  const { run } = useDispatchAction();

  const fetcher = useCallback(async ({ page, q }: { page: number; q: string }): Promise<Page<User>> => {
    return UsersAPI.list(q, page);
  }, []);
  const q = usePagedQuery<User, Record<string, never>>({ fetcher, initialFilters: {}, pageSize: 25 });

  async function performAction() {
    if (!acting) return;
    const { user, kind } = acting;
    await run(kind === 'suspend' ? 'Suspend user' : 'Restore user',
      () => kind === 'suspend' ? UsersAPI.suspend(user.id, 'Admin action') : UsersAPI.unsuspend(user.id, 'Admin action'));
    setActing(null);
    q.refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="04 — USERS"
        title={<>The <em>roster</em>.</>}
        lede="Every account on the platform. Search by email, role, or id. Suspend stops sign-in immediately; restore reverses it. Both write audit entries."
        actions={<SearchInput value={q.q} onChange={q.setQ} placeholder="Email, name, id…" />}
      />

      {q.error && <NotImplementedBanner endpoint="/api/admin/users" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="No users found" hint={q.q ? `Nothing matches "${q.q}".` : 'No users on record.'} /></Card> :
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map(u => (
                <tr key={u.id}>
                  <td><Link to={`/users/${encodeURIComponent(u.id)}`} className="nb-table__primary nb-table__link">{u.email}</Link></td>
                  <td><span className="nb-table__mono">{u.roles.join(' · ')}</span></td>
                  <td>
                    {u.disabled ? <StatusPill tone="rust">Suspended</StatusPill> :
                     u.subscription?.status === 'ACTIVE' ? <StatusPill tone="moss">Active</StatusPill> :
                     <StatusPill tone="brass">Free</StatusPill>}
                  </td>
                  <td className="nb-table__mono">{formatDate(u.createdAt)}</td>
                  <td className="nb-table__align-right">
                    <div className="nb-row-actions">
                      <Button size="sm" onClick={() => setMessaging(u)}>✉ Message</Button>
                      <Button size="sm" onClick={() => setGifting(u)}>🎁 Gift</Button>
                      {u.disabled
                        ? <Button size="sm" onClick={() => setActing({ user: u, kind: 'unsuspend' })}>Restore</Button>
                        : <Button size="sm" tone="danger" onClick={() => setActing({ user: u, kind: 'suspend' })}>Suspend</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }

      <ConfirmDialog
        open={!!acting}
        title={acting?.kind === 'suspend' ? 'Suspend account' : 'Restore account'}
        message={acting?.kind === 'suspend'
          ? `Suspending ${acting?.user.email} blocks sign-in immediately. They will see a generic "account unavailable" message.`
          : `Restoring ${acting?.user.email} re-enables sign-in.`}
        requireReason
        destructive={acting?.kind === 'suspend'}
        confirmLabel={acting?.kind === 'suspend' ? 'Suspend' : 'Restore'}
        onClose={() => setActing(null)}
        onConfirm={performAction}
      />

      <ComposeBroadcastDialog
        open={!!messaging}
        onClose={() => setMessaging(null)}
        onSent={() => setMessaging(null)}
        lockedRecipient={messaging ? {
          email: messaging.email,
          name: [messaging.firstname, messaging.lastname].filter(Boolean).join(' ') || undefined,
        } : undefined}
      />

      <GiftDialog
        open={!!gifting}
        onClose={() => setGifting(null)}
        onGifted={() => { setGifting(null); q.refresh(); }}
        lockedEmail={gifting?.email}
      />
    </div>
  );
}

export function NotImplementedBanner({ endpoint }: { endpoint: string }) {
  return (
    <Card>
      <div className="nb-banner">
        <strong className="nb-banner__title">Endpoint pending.</strong>{' '}
        This page needs <code className="nb-banner__code">{endpoint}</code> on the backend. Once shipped, this page lights up automatically.
      </div>
    </Card>
  );
}
